import type { AxiosError } from 'axios'
import { type Ref, ref, toValue, watchEffect } from 'vue'

import { normalizeAdvertiserItems } from '@/lib/adapters/advertiser.adapter'
import { normalizeReturnAd, serializeReturnAd } from '@/lib/adapters/return-ad.adapter'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import type { AdvertiserType } from '@/lib/types/advertiser.types'
import type { ReturnAdFormType } from '@/lib/types/return-ad.types'
import { useErrorStore } from '@/stores/error'

export const useGetReturnAdRequest = (id: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<ReturnAdFormType | null>(null)
  const errorStore = useErrorStore()

  const getReturnAd = async () => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get(`/v2/return-ads/${id}`)
      data.value = normalizeReturnAd(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watchEffect(() => void getReturnAd())

  return {
    data,
    getReturnAd,
    isLoading,
    error,
  }
}

export const useCreateReturnAdRequest = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const createAd = async (adFormData: ReturnAdFormType) => {
    const data = serializeReturnAd(adFormData)
    try {
      isLoading.value = true
      error.value = null
      await request.post('/v2/return-ads', data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    createAd,
    isLoading,
    error,
  }
}

export const useEditReturnAdRequest = (returnAdId: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const editReturnAd = async (adFormData: ReturnAdFormType) => {
    const data = serializeReturnAd(adFormData)
    try {
      isLoading.value = true
      error.value = null
      await request.put(`/v2/return-ads/${returnAdId}`, data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    editReturnAd,
    isLoading,
    error,
  }
}

export const useGetAdLinkDataRequest = (returnAdUserId: Ref<number | undefined>) => {
  const isLoading = ref(false)
  const isConverted = ref(false)
  const error = ref<string | undefined>(undefined)
  const errorStore = useErrorStore()
  const data = ref<AdvertiserType[]>()

  const getLinkData = async (link: string) => {
    const userId = toValue(returnAdUserId)
    if (userId === undefined) {
      data.value = undefined
      error.value = 'Unable to inspect link for the selected advertiser group'
      return
    }
    try {
      isLoading.value = true
      isConverted.value = false
      error.value = undefined
      const response = await request.post(`/v2/users/${userId}/inspections/affiliate-link`, {
        url: toValue(link),
        ignore_advertiser_visibility: true,
      })
      data.value = normalizeAdvertiserItems(response.data.matching_advertisers ?? [])
      if (!data.value.length) {
        error.value = "This link doesn't match any advertiser in the selected advertiser group"
      }
    } catch (e: unknown) {
      const axiosError = e as AxiosError<{
        status: string
      }>
      if (axiosError?.response?.status !== 400 && axiosError.response?.data?.status !== 'failed') {
        errorStore.setError(handleErrorMessage(e))
      }
      error.value = 'This link is not a valid product link'
    } finally {
      isLoading.value = false
    }
  }

  return {
    data,
    getLinkData,
    isLoading,
    error,
    isConverted,
  }
}

export const useSetReturnAdActivityRequest = (returnAdId: number) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const setStatus = async (shouldPause: boolean): Promise<undefined> => {
    try {
      error.value = null
      isLoading.value = true
      const url = shouldPause
        ? `/v2/return-ads/${returnAdId}/pause`
        : `/v2/return-ads/${returnAdId}/unpause`
      const { data } = await request.post(url, {})
      return data.status
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    setStatus,
    isLoading,
    error,
  }
}
